import type { ApiOperations, OperationName } from './generated/client.js';
import { operations } from './generated/client.js';
import type { ErrorEnvelope } from './common.js';
import { API_PREFIX } from './openapi.js';

export type { ApiOperations, OperationName };
export { operations };

/** Error raised for non-2xx API responses, carrying the standard error envelope. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly envelope: ErrorEnvelope | undefined,
    readonly retryAfterSeconds?: number,
  ) {
    super(envelope?.error.message ?? `HTTP ${status}`);
    this.name = 'ApiError';
  }
  get code() {
    return this.envelope?.error.code;
  }
}

type Args<K extends OperationName> = (ApiOperations[K]['params'] extends undefined ? { params?: undefined } : { params: ApiOperations[K]['params'] }) &
  (ApiOperations[K]['query'] extends undefined ? { query?: undefined } : { query?: ApiOperations[K]['query'] }) &
  (ApiOperations[K]['body'] extends undefined ? { body?: undefined } : { body: ApiOperations[K]['body'] }) & {
    idempotencyKey?: string;
    signal?: AbortSignal;
  };

export interface ApiClientOptions {
  baseUrl?: string;
  fetch?: typeof fetch;
  /** Extra headers per request, e.g. CSRF token or bearer token. */
  headers?: () => Record<string, string>;
  credentials?: 'include' | 'same-origin' | 'omit';
}

export function buildPath(template: string, params: Record<string, string> | undefined): string {
  return template.replace(/:([A-Za-z]+)/g, (_, name: string) => {
    const value = params?.[name];
    if (value === undefined) throw new Error(`Missing path parameter ${name}`);
    return encodeURIComponent(value);
  });
}

/** Typed fetch client generated from the shared route registry. */
export function createApiClient(options: ApiClientOptions = {}) {
  const doFetch = options.fetch ?? fetch;
  async function call<K extends OperationName>(name: K, args: Args<K>): Promise<ApiOperations[K]['response']> {
    const op = operations[name];
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries((args.query ?? {}) as Record<string, unknown>)) {
      if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
    }
    const qs = query.toString();
    const url = `${options.baseUrl ?? ''}${API_PREFIX}${buildPath(op.path, args.params as Record<string, string> | undefined)}${qs ? `?${qs}` : ''}`;
    const headers: Record<string, string> = { Accept: 'application/json', ...(options.headers?.() ?? {}) };
    if (args.body !== undefined) headers['Content-Type'] = 'application/json';
    if (args.idempotencyKey) headers['Idempotency-Key'] = args.idempotencyKey;
    const response = await doFetch(url, {
      method: op.method,
      headers,
      body: args.body !== undefined ? JSON.stringify(args.body) : undefined,
      credentials: options.credentials ?? 'same-origin',
      signal: args.signal,
    });
    if (response.status === 204) return undefined as ApiOperations[K]['response'];
    const text = await response.text();
    const json = text ? (JSON.parse(text) as unknown) : undefined;
    if (!response.ok) {
      const retry = response.headers.get('Retry-After');
      throw new ApiError(response.status, json as ErrorEnvelope | undefined, retry ? Number(retry) : undefined);
    }
    return json as ApiOperations[K]['response'];
  }
  return { call };
}

export type ApiClient = ReturnType<typeof createApiClient>;
